import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import { useTranslation } from "react-i18next";
import { I } from "@/components/design";
import { useProfile } from "@/hooks/useProfile";
import { setActiveMerchantId } from "@/lib/api";
import { DEFAULT_AUTHENTICATED_HOME } from "@/lib/routes";
import { space } from "@/theme/foundation";
import type { MerchantMembership } from "@/types/profile";

function switchTo(merchantId: string): void {
  setActiveMerchantId(merchantId, { persist: true });
  // Hard reload so every in-memory cache (subscription, profile,
  // conversations, etc.) re-fetches under the new merchant context.
  // Same pattern as handleSignOut — simpler than per-hook invalidation.
  if (typeof window !== "undefined") {
    window.location.assign(DEFAULT_AUTHENTICATED_HOME);
  }
}

export function MerchantSwitcher() {
  const { t } = useTranslation("common");
  const { profile } = useProfile();
  const memberships = profile?.memberships ?? [];

  // Render-time invariants:
  //   - 0 memberships → render nothing (caller's fallback wordmark shows)
  //   - 1 membership → plain text, no chevron
  //   - 2+ memberships → dropdown with check-mark on the current one
  if (memberships.length === 0) return null;

  const current = profile?.merchantId ?? memberships[0]?.merchantId ?? null;
  const currentName =
    memberships.find((m: MerchantMembership) => m.merchantId === current)?.merchantName ??
    profile?.merchantName ??
    memberships[0]?.merchantName ??
    t("merchant.myStore");

  if (memberships.length === 1) {
    return (
      <div className="sidebar-trigger" title={currentName} aria-disabled="true">
        <span className="sidebar-trigger-label">{currentName}</span>
      </div>
    );
  }

  const items: MenuProps["items"] = memberships.map((m: MerchantMembership) => ({
    key: m.merchantId,
    label: (
      <div
        className="row"
        style={{
          alignItems: "center",
          gap: space[2],
          minWidth: 200,
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: m.merchantId === current ? 700 : 500 }}>{m.merchantName ?? m.merchantId}</span>
        {m.merchantId === current && <I.check size={14} stroke="var(--primary)" strokeWidth={2.4} />}
      </div>
    ),
    onClick: () => {
      if (m.merchantId !== current) switchTo(m.merchantId);
    },
  }));

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomLeft">
      <button type="button" className="sidebar-trigger" title={currentName} aria-label={t("merchant.switchStore")}>
        <span className="sidebar-trigger-label">{currentName}</span>
        <I.down size={10} />
      </button>
    </Dropdown>
  );
}
